import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit, FiSave, FiX, FiKey, FiUsers, FiDollarSign,
  FiCalendar, FiMail, FiPhone, FiMapPin, FiActivity, FiCheckCircle,
  FiAlertTriangle, FiClock, FiDownload, FiTrash2, FiCpu, FiSliders
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@components/common';

// Types
interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  subscription: {
    plan: 'starter' | 'professional' | 'premium' | 'enterprise';
    status: 'active' | 'trial' | 'expired' | 'suspended';
    startDate: string;
    endDate: string;
    monthlyPrice: number;
    users: number;
    maxUsers: number;
    autoRenewal: boolean;
  };
  license: {
    key: string;
    issued: string;
    expires: string;
    features: string[];
  };
  usage: {
    applications: number;
    candidates: number;
    users: number;
    storage: number;
  };
  billing: {
    totalPaid: number;
    lastPayment: string;
    nextPayment: string;
    paymentMethod: string;
  };
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone: string;
  };
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const BackButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: white;
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0;
`;

const Subtitle = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const StatusBanner = styled.div<{ status: string }>`
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => {
    switch (props.status) {
      case 'active': return `${theme.colors.success}20`;
      case 'trial': return `${theme.colors.info}20`;
      case 'expired': return `${theme.colors.error}20`;
      case 'suspended': return `${theme.colors.warning}20`;
      default: return theme.colors.surface;
    }
  }};
  border-left: 4px solid ${props => {
    switch (props.status) {
      case 'active': return theme.colors.success;
      case 'trial': return theme.colors.info;
      case 'expired': return theme.colors.error;
      case 'suspended': return theme.colors.warning;
      default: return theme.colors.surface;
    }
  }};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusInfo = styled.div``;

const StatusLabel = styled.div`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}70;
  margin-bottom: ${theme.spacing.xs};
`;

const StatusValue = styled.div`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const InfoLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const InfoValue = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const LicenseBox = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  border: 2px dashed ${theme.colors.surface};
  font-family: 'Courier New', monospace;
  font-size: ${theme.typography.sizes.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.surface}30;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const StatBox = styled.div`
  padding: ${theme.spacing.lg};
  background: white;
  border-radius: ${theme.borderRadius.md};
  text-align: center;
  margin-bottom: ${theme.spacing.md};
`;

const StatBoxValue = styled.div`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const StatBoxLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
`;

const PlanBadge = styled.span<{ plan: string }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};

  ${props => {
    switch (props.plan) {
      case 'starter':
        return `
          background: ${theme.colors.primary};
          color: white;
        `;
      case 'professional':
        return `
          background: ${theme.colors.primary};
          color: white;
        `;
      case 'premium':
        return `
          background: ${theme.colors.accent};
          color: white;
        `;
      case 'enterprise':
        return `
          background: ${theme.colors.info};
          color: white;
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
        `;
    }
  }}
`;

export const ClientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);

  // Mock data - à remplacer par des données réelles
  const client: Client = {
    id: id || 'CLI-001',
    name: 'Office HLM Paris',
    email: 'contact@hlm-paris.fr',
    phone: '+33 1 42 00 00 00',
    address: '123 Avenue de la République, 75011 Paris',
    subscription: {
      plan: 'enterprise',
      status: 'active',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      monthlyPrice: 1290,
      users: 48,
      maxUsers: 50,
      autoRenewal: true
    },
    license: {
      key: 'PRE-2024-A3F8E2',
      issued: '2024-01-01',
      expires: '2024-12-31',
      features: [
        'API Access',
        'White Label',
        'Multi-site',
        'Advanced Analytics',
        'Custom Integrations',
        'Priority Support',
        'Custom Reports',
        'SSO Integration'
      ]
    },
    usage: {
      applications: 1247,
      candidates: 3891,
      users: 48,
      storage: 127.5
    },
    billing: {
      totalPaid: 15480,
      lastPayment: '2024-01-01',
      nextPayment: '2024-02-01',
      paymentMethod: 'Virement bancaire'
    },
    contact: {
      primaryName: 'Jean Dupont',
      primaryEmail: 'j.dupont@hlm-paris.fr',
      primaryPhone: '+33 1 42 00 00 01'
    }
  };

  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'starter': return 'Starter';
      case 'professional': return 'Professional';
      case 'premium': return 'Premium';
      case 'enterprise': return 'Enterprise';
      default: return plan;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'trial': return 'Essai';
      case 'expired': return 'Expiré';
      case 'suspended': return 'Suspendu';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <FiCheckCircle />;
      case 'trial': return <FiClock />;
      case 'expired': return <FiAlertTriangle />;
      case 'suspended': return <FiAlertTriangle />;
      default: return <FiActivity />;
    }
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate('/superadmin/clients')}>
              <FiArrowLeft /> Retour
            </BackButton>
            <div>
              <Title>{client.name}</Title>
              <Subtitle>ID: {client.id}</Subtitle>
            </div>
          </HeaderLeft>
          <ActionButtons>
            {isEditing ? (
              <>
                <Button variant="primary" onClick={() => setIsEditing(false)}>
                  <FiSave /> Enregistrer
                </Button>
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  <FiX /> Annuler
                </Button>
              </>
            ) : (
              <>
                <Button variant="primary" onClick={() => setIsEditing(true)}>
                  <FiEdit /> Modifier
                </Button>
                <Button variant="outline">
                  <FiDownload /> Exporter
                </Button>
                <Button variant="outline" onClick={() => navigate(`/superadmin/licenses/generate?client=${client.id}`)}>
                  <FiKey /> Nouvelle Licence
                </Button>
              </>
            )}
          </ActionButtons>
        </Header>

        <StatusBanner status={client.subscription.status}>
          <StatusInfo>
            <StatusLabel>Statut de l'abonnement</StatusLabel>
            <StatusValue>
              {getStatusIcon(client.subscription.status)}
              {getStatusLabel(client.subscription.status)}
              {' • '}
              <PlanBadge plan={client.subscription.plan}>
                {getPlanLabel(client.subscription.plan)}
              </PlanBadge>
            </StatusValue>
          </StatusInfo>
          <div style={{ fontSize: theme.typography.sizes['2xl'], fontWeight: theme.typography.weights.bold, color: theme.colors.primary }}>
            {client.subscription.monthlyPrice} FCFA/mois
          </div>
        </StatusBanner>

        <ContentGrid>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
            <Card>
              <CardTitle>
                <FiUsers /> Informations Client
              </CardTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel><FiMail /> Email</InfoLabel>
                  <InfoValue>{client.email}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel><FiPhone /> Téléphone</InfoLabel>
                  <InfoValue>{client.phone}</InfoValue>
                </InfoItem>
                <InfoItem style={{ gridColumn: '1 / -1' }}>
                  <InfoLabel><FiMapPin /> Adresse</InfoLabel>
                  <InfoValue>{client.address}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Contact Principal</InfoLabel>
                  <InfoValue>{client.contact.primaryName}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Email Contact</InfoLabel>
                  <InfoValue>{client.contact.primaryEmail}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Card>

            <Card>
              <CardTitle>
                <FiKey /> Licence & Fonctionnalités
              </CardTitle>
              <InfoLabel>Clé de Licence</InfoLabel>
              <LicenseBox>
                <code>{client.license.key}</code>
                <Button variant="outline" size="sm">
                  <FiDownload /> Copier
                </Button>
              </LicenseBox>
              <InfoGrid style={{ marginTop: theme.spacing.lg }}>
                <InfoItem>
                  <InfoLabel><FiCalendar /> Émise le</InfoLabel>
                  <InfoValue>{new Date(client.license.issued).toLocaleDateString('fr-FR')}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel><FiCalendar /> Expire le</InfoLabel>
                  <InfoValue>{new Date(client.license.expires).toLocaleDateString('fr-FR')}</InfoValue>
                </InfoItem>
              </InfoGrid>
              <InfoLabel style={{ marginTop: theme.spacing.lg }}>Fonctionnalités Activées</InfoLabel>
              <FeaturesList>
                {client.license.features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <FiCheckCircle style={{ color: theme.colors.success }} />
                    {feature}
                  </FeatureItem>
                ))}
              </FeaturesList>
            </Card>

            <Card>
              <CardTitle>
                <FiDollarSign /> Facturation
              </CardTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Total Payé</InfoLabel>
                  <InfoValue style={{ color: theme.colors.success }}>
                    {client.billing.totalPaid.toLocaleString()} FCFA
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Méthode de Paiement</InfoLabel>
                  <InfoValue>{client.billing.paymentMethod}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Dernier Paiement</InfoLabel>
                  <InfoValue>{new Date(client.billing.lastPayment).toLocaleDateString('fr-FR')}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Prochain Paiement</InfoLabel>
                  <InfoValue>{new Date(client.billing.nextPayment).toLocaleDateString('fr-FR')}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Card>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
            <Card>
              <CardTitle>
                <FiActivity /> Utilisation
              </CardTitle>
              <StatBox>
                <StatBoxValue>{client.usage.applications.toLocaleString()}</StatBoxValue>
                <StatBoxLabel>Candidatures Traitées</StatBoxLabel>
              </StatBox>
              <StatBox>
                <StatBoxValue>{client.usage.candidates.toLocaleString()}</StatBoxValue>
                <StatBoxLabel>Candidats Enregistrés</StatBoxLabel>
              </StatBox>
              <StatBox>
                <StatBoxValue>{client.usage.users} / {client.subscription.maxUsers}</StatBoxValue>
                <StatBoxLabel>Utilisateurs Actifs</StatBoxLabel>
              </StatBox>
              <StatBox>
                <StatBoxValue>{client.usage.storage} GB</StatBoxValue>
                <StatBoxLabel>Espace de Stockage</StatBoxLabel>
              </StatBox>
            </Card>

            <Card>
              <CardTitle>Actions Rapides</CardTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <Button
                  variant="primary"
                  onClick={() => navigate(`/superadmin/ai-config/${client.id}`)}
                  style={{ marginBottom: theme.spacing.sm }}
                >
                  <FiCpu /> Configuration IA & Scoring
                </Button>
                <Button variant="outline" onClick={() => navigate(`/superadmin/licenses/generate?client=${client.id}`)}>
                  <FiKey /> Renouveler Licence
                </Button>
                <Button variant="outline">
                  <FiEdit /> Modifier Abonnement
                </Button>
                <Button variant="outline">
                  <FiMail /> Envoyer Email
                </Button>
                <Button variant="outline">
                  <FiDownload /> Télécharger Rapport
                </Button>
                <Button variant="outline" style={{ color: theme.colors.error, borderColor: theme.colors.error }}>
                  <FiTrash2 /> Suspendre Compte
                </Button>
              </div>
            </Card>
          </div>
        </ContentGrid>
      </Container>
    </DashboardLayout>
  );
};
